import type { TypographyVariant } from '@/components/ui/typography';
import { H1, H2, H3, H4 } from '@/components/ui/typography';

export const HEADING_REGEX = /^(#{1,4})\s+(.+)$/;
export const HORIZONTAL_RULE_REGEX = /^-{3,}$/;
export const UNORDERED_LIST_REGEX = /^- (.+)$/;
export const ORDERED_LIST_REGEX = /^(\d+)\. (.+)$/;

export const HEADING_CONFIG: Record<number, { Component: typeof H1; variant: TypographyVariant }> = {
    1: { Component: H1, variant: 'type-subheading-medium' },
    2: { Component: H2, variant: 'type-subheading-small' },
    3: { Component: H3, variant: 'type-subheading-xsmall-semibold' },
    4: { Component: H4, variant: 'type-subheading-xsmall' },
};

export type Block =
    | { type: 'heading'; line: string; lineIndex: number }
    | { type: 'hr'; lineIndex: number }
    | { type: 'text'; line: string; lineIndex: number }
    | { type: 'blank'; lineIndex: number }
    | { type: 'ul'; items: { line: string; lineIndex: number }[] }
    | { type: 'ol'; startNumber: number; items: { line: string; lineIndex: number }[] };

export const hasBlockSyntax = (lines: string[]): boolean =>
    lines.some(
        line =>
            HEADING_REGEX.test(line)
            || HORIZONTAL_RULE_REGEX.test(line.trim())
            || UNORDERED_LIST_REGEX.test(line)
            || ORDERED_LIST_REGEX.test(line),
    );

export const parseBlocks = (lines: string[]): Block[] => {
    const blocks: Block[] = [];

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const ulMatch = line.match(UNORDERED_LIST_REGEX);
        const olMatch = line.match(ORDERED_LIST_REGEX);

        if (ulMatch) {
            const last = blocks[blocks.length - 1];
            if (last && last.type === 'ul') {
                last.items.push({ line: ulMatch[1], lineIndex: i });
            }
            else {
                blocks.push({ type: 'ul', items: [{ line: ulMatch[1], lineIndex: i }] });
            }
        }
        else if (olMatch) {
            const last = blocks[blocks.length - 1];
            if (last && last.type === 'ol') {
                last.items.push({ line: olMatch[2], lineIndex: i });
            }
            else {
                blocks.push({ type: 'ol', startNumber: parseInt(olMatch[1], 10), items: [{ line: olMatch[2], lineIndex: i }] });
            }
        }
        else if (HORIZONTAL_RULE_REGEX.test(line.trim())) {
            blocks.push({ type: 'hr', lineIndex: i });
        }
        else if (HEADING_REGEX.test(line)) {
            blocks.push({ type: 'heading', line, lineIndex: i });
        }
        else if (line.trim() === '') {
            const last = blocks[blocks.length - 1];
            const nextNonBlank = lines.slice(i + 1).find(l => l.trim() !== '');
            const nextContinuesList
                = nextNonBlank
                    && ((last?.type === 'ul' && UNORDERED_LIST_REGEX.test(nextNonBlank))
                        || (last?.type === 'ol' && ORDERED_LIST_REGEX.test(nextNonBlank)));
            if (!nextContinuesList) {
                blocks.push({ type: 'blank', lineIndex: i });
            }
        }
        else {
            blocks.push({ type: 'text', line, lineIndex: i });
        }
    }

    return blocks;
};
